import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Package, Truck, ArrowLeft, ArrowRight, Plus, Minus, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';

const AdditionalServices = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    selectedDate, 
    hourlyRate, 
    propertyType, 
    propertySize, 
    floorType, 
    crewRecommendation,
    originAddress,
    destinationAddress,
    routeData
  } = location.state || {};

  const [selectedServices, setSelectedServices] = useState({
    packingOnly: false,
    fullPacking: false,
    bulkyItems: false,
    storage: false,
    disassembly: false,
    cleaning: false
  });

  const [bulkyItemsCount, setBulkyItemsCount] = useState(1);
  const [additionalDetails, setAdditionalDetails] = useState('');

  const additionalServicesOptions = [
    {
      id: 'packingOnly',
      name: 'Packing Only',
      description: 'Professional packing service (no materials included)',
      price: 0,
      priceDisplay: 'Quote on Request',
      icon: Package
    },
    {
      id: 'fullPacking',
      name: 'Full Packing Service',
      description: 'Boxes, tape, bubble wrap, and packing supplies included',
      price: 0,
      priceDisplay: 'Quote on Request',
      icon: Package
    },
    {
      id: 'bulkyItems',
      name: 'Bulky Items Handling',
      description: 'Piano, safe, hot tub, gym equipment (per item)',
      price: 0,
      priceDisplay: 'Quote on Request',
      icon: Truck,
      quantity: true
    },
    {
      id: 'storage',
      name: 'Temporary Storage',
      description: '30-day storage facility access',
      price: 250,
      priceDisplay: '$250/night',
      icon: Home
    },
    {
      id: 'disassembly',
      name: 'Furniture Disassembly/Assembly',
      description: 'Taking apart and reassembling furniture',
      price: 0,
      priceDisplay: 'Included',
      icon: Home,
      included: true
    },
    {
      id: 'cleaning',
      name: 'Post-Move Cleaning',
      description: 'New service - Coming Soon!',
      price: 0,
      priceDisplay: 'Coming Soon',
      icon: Home,
      comingSoon: true
    }
  ];

  const handleServiceToggle = (serviceId) => {
    setSelectedServices(prev => ({
      ...prev,
      [serviceId]: !prev[serviceId]
    }));
  };

  const calculateAdditionalCosts = () => {
    let total = 0;
    Object.entries(selectedServices).forEach(([serviceId, isSelected]) => {
      if (isSelected) {
        const service = additionalServicesOptions.find(s => s.id === serviceId);
        if (service && service.price > 0) {
          if (serviceId === 'bulkyItems') {
            total += service.price * bulkyItemsCount;
          } else {
            total += service.price;
          }
        }
      }
    });
    return total;
  };

  const handleContinue = () => {
    const additionalCosts = calculateAdditionalCosts();
    
    navigate('/final-quote', {
      state: {
        selectedDate,
        hourlyRate,
        propertyType,
        propertySize,
        floorType,
        crewRecommendation,
        originAddress,
        destinationAddress,
        routeData,
        additionalServices: selectedServices,
        bulkyItemsCount,
        additionalDetails,
        additionalCosts
      }
    });
  };

  const handleGoBack = () => {
    navigate('/route-map', { 
      state: {
        selectedDate,
        hourlyRate,
        propertyType,
        propertySize,
        floorType,
        crewRecommendation,
        originAddress,
        destinationAddress
      },
      replace: true
    });
  };

  return (
    <div className="min-h-screen" style={{backgroundColor: '#F8F7F4'}}>
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center gap-3 cursor-pointer group">
              <img 
                src="https://customer-assets.emergentagent.com/job_swiftmover/artifacts/7o75lsp7_Emisa%20Movers%20Logo.jpeg" 
                alt="Emisa Movers Logo"
                className="h-28 w-28 transition-all duration-300 transform group-hover:scale-110 group-hover:shadow-2xl group-active:scale-105 rounded-lg"
              />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Emisa Movers</h1>
                <p className="text-xs text-amber-700 font-medium">GETS IT DONE ON TIME</p>
              </div>
            </a>
            <div className="flex items-center gap-3">
              <a 
                href="tel:7134879097"
                className="flex items-center gap-2 bg-red-600 hover:bg-red-700 hover:shadow-2xl hover:scale-110 text-white px-4 py-2 rounded-lg font-medium shadow-md transition-all duration-300 transform"
              >
                <Phone className="h-4 w-4" />
                (713) 487-9097
              </a>
              <a href="/" onClick={(e) => { e.preventDefault(); window.location.href = '/#book-now'; }} className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-300">Book It NOW</a>
              <Button
                variant="outline"
                onClick={handleGoBack}
                className="flex items-center gap-2 bg-gray-600 hover:bg-gray-700 text-white border-gray-600"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Route
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Booking Info */}
        <div className="bg-white border border-gray-200 rounded-lg p-2 mb-3 shadow-sm">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
            <div>
              <span className="text-gray-600">Date:</span>
              <div className="font-medium text-teal-600 text-xs">{selectedDate}</div>
            </div>
            <div>
              <span className="text-gray-600">Rate:</span>
              <div className="font-medium text-red-600 text-xs">${hourlyRate}/h</div>
            </div>
            <div>
              <span className="text-gray-600">Property:</span>
              <div className="font-medium text-teal-600 text-xs capitalize">{propertyType}</div>
            </div>
            <div>
              <span className="text-gray-600">Size:</span>
              <div className="font-medium text-teal-600 text-xs">{propertySize} sqft</div>
            </div>
            <div>
              <span className="text-gray-600">Crew:</span>
              <div className="font-medium text-teal-600 text-xs">{crewRecommendation?.men} movers</div>
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="text-center mb-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Additional Services
          </h1>
          <p className="text-sm sm:text-base text-gray-700">
            Add extra services to make your move even smoother
          </p>
        </div>

        {/* Additional Services */}
        <div className="mb-4">
          <h2 className="text-xl font-semibold text-teal-600 mb-3">Available Services</h2>
          <div className="grid md:grid-cols-2 gap-3">
            {additionalServicesOptions.map((service) => {
              const Icon = service.icon;
              const isSelected = selectedServices[service.id];
              
              return (
                <Card
                  key={service.id}
                  className={`cursor-pointer transition-all duration-300 transform hover:shadow-lg hover:-translate-y-1 hover:scale-105 ${
                    service.comingSoon 
                      ? 'border-gray-300 bg-gray-50 cursor-not-allowed opacity-75'
                      : isSelected
                      ? 'border-teal-600 bg-teal-50 shadow-md'
                      : 'border-gray-200 hover:border-teal-600'
                  }`}
                  onClick={() => !service.comingSoon && handleServiceToggle(service.id)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-start gap-2">
                      <Checkbox
                        checked={isSelected}
                        onChange={() => {}}
                        className="mt-0.5"
                        disabled={service.comingSoon}
                      />
                      <Icon className={`h-5 w-5 mt-0.5 ${
                        service.comingSoon 
                          ? 'text-gray-400'
                          : isSelected ? 'text-teal-600' : 'text-gray-400'
                      }`} />
                      <div className="flex-1">
                        <div className="flex justify-between items-start mb-1">
                          <h3 className={`font-semibold text-sm ${
                            service.comingSoon ? 'text-gray-500' : 'text-teal-600'
                          }`}>{service.name}</h3>
                          <div className="text-right">
                            <span className={`font-bold text-sm ${
                              service.included 
                                ? 'text-teal-600' 
                                : service.comingSoon 
                                ? 'text-gray-500'
                                : 'text-red-600'
                            }`}>
                              {service.priceDisplay}
                            </span>
                            {service.quantity && !service.comingSoon && (
                              <span className="text-xs text-gray-600">/item</span>
                            )}
                          </div>
                        </div>
                        <p className={`text-xs ${
                          service.comingSoon ? 'text-gray-500' : 'text-gray-600'
                        }`}>{service.description}</p>
                        
                        {/* Quantity selector for bulky items */}
                        {service.id === 'bulkyItems' && isSelected && !service.comingSoon && (
                          <div className="mt-2 flex items-center gap-2">
                            <span className="text-xs text-gray-600">Qty:</span>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setBulkyItemsCount(Math.max(1, bulkyItemsCount - 1));
                                }}
                                className="h-6 w-6 p-0"
                              >
                                <Minus className="h-3 w-3" />
                              </Button>
                              <span className="w-6 text-center font-medium text-teal-600 text-sm">{bulkyItemsCount}</span>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setBulkyItemsCount(bulkyItemsCount + 1);
                                }}
                                className="h-6 w-6 p-0"
                              >
                                <Plus className="h-3 w-3" />
                              </Button>
                            </div>
                            <span className="text-xs text-teal-600">
                              {bulkyItemsCount} item{bulkyItemsCount > 1 ? 's' : ''}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Additional Details */}
        <div className="mb-4">
          <Card>
            <CardHeader className="p-3">
              <CardTitle className="text-teal-600 text-base">Additional Details</CardTitle>
            </CardHeader>
            <CardContent className="p-3 pt-0">
              <div>
                <Label htmlFor="additionalDetails" className="text-sm">Special Instructions or Requirements</Label>
                <Textarea
                  id="additionalDetails"
                  placeholder="Please provide any additional details about your move, special handling requirements, access codes, parking restrictions, valuable items, etc."
                  value={additionalDetails}
                  onChange={(e) => setAdditionalDetails(e.target.value)}
                  className="mt-1 focus:ring-teal-600 focus:border-teal-600 text-sm"
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Cost Summary */}
        {(calculateAdditionalCosts() > 0 || Object.values(selectedServices).some(Boolean)) && (
          <div className="mb-4">
            <Card className="border-gray-200 bg-white shadow-sm">
              <CardHeader className="p-3">
                <CardTitle className="text-teal-600 text-base">Selected Additional Services</CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0">
                <div className="space-y-1.5 mb-3">
                  {Object.entries(selectedServices).map(([serviceId, isSelected]) => {
                    if (!isSelected) return null;
                    const service = additionalServicesOptions.find(s => s.id === serviceId);
                    if (!service) return null;
                    
                    let displayText = service.name;
                    if (serviceId === 'bulkyItems') {
                      displayText += ` (×${bulkyItemsCount})`;
                    }
                    
                    return (
                      <div key={serviceId} className="flex justify-between items-center text-xs">
                        <span className="text-gray-700">{displayText}</span>
                        <span className={`font-medium ${
                          service.included ? 'text-teal-600' : 
                          service.comingSoon ? 'text-gray-500' :
                          service.price === 0 ? 'text-red-600' : 'text-gray-700'
                        }`}>
                          {service.priceDisplay}
                        </span>
                      </div>
                    );
                  })}
                </div>
                {calculateAdditionalCosts() > 0 && (
                  <div className="border-t pt-2 flex justify-between items-center font-bold text-teal-600 text-sm">
                    <span>Additional Services Total:</span>
                    <span>${calculateAdditionalCosts()}</span>
                  </div>
                )}
                {calculateAdditionalCosts() === 0 && Object.values(selectedServices).some(Boolean) && (
                  <div className="border-t pt-2 text-center text-teal-600 font-medium text-xs">
                    <span>Services selected - Quote will be provided upon request</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Continue Button */}
        <div className="text-center">
          <Button
            onClick={handleContinue}
            size="lg"
            className="bg-red-600 hover:bg-red-700 hover:shadow-xl hover:-translate-y-1 hover:scale-105 text-white px-6 py-2 text-base transition-all duration-300 transform"
          >
            Continue to Final Quote
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdditionalServices;